import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { EmailConsentText } from "./email-consent-text";
import { Loader2Icon, ArrowRightIcon, MailIcon } from "lucide-react";

interface EmailStepProps {
  onSubmit: (email: string) => Promise<void>;
  isLoading: boolean;
  error: string | null;
}

export function EmailStep({ onSubmit, isLoading, error }: EmailStepProps) {
  const [email, setEmail] = useState("");
  const [isFocused, setIsFocused] = useState(false);
  const [touched, setTouched] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (email.trim()) {
      await onSubmit(email.trim().toLowerCase());
    }
  };

  const isValidEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  const showValidationError = touched && email.length > 0 && !isValidEmail;

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="space-y-3">
        <Label
          htmlFor="email"
          className="text-sm font-medium tracking-wide uppercase text-muted-foreground"
        >
          Email address
        </Label>

        {/* Custom styled input with animated border */}
        <div className="relative group">
          <div
            className={`
              absolute -inset-px rounded-lg transition-all duration-300
              ${
                isFocused
                  ? "bg-gradient-to-r from-primary/20 via-primary/40 to-primary/20"
                  : "bg-transparent"
              }
            `}
          />
          <div className="relative flex items-center">
            <MailIcon
              className={`
                absolute left-4 size-4 transition-colors duration-200
                ${isFocused ? "text-foreground" : "text-muted-foreground"}
              `}
            />
            <input
              id="email"
              type="email"
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onFocus={() => setIsFocused(true)}
              onBlur={() => {
                setIsFocused(false);
                setTouched(true);
              }}
              disabled={isLoading}
              autoComplete="email"
              autoFocus
              className={`
                w-full h-12 pl-11 pr-4 rounded-lg
                bg-card border border-border
                text-foreground placeholder:text-muted-foreground/60
                font-sans text-base
                outline-none transition-all duration-200
                focus:border-primary/50
                disabled:opacity-50 disabled:cursor-not-allowed
              `}
            />
          </div>
        </div>

        {/* Validation error - shown after blur */}
        {showValidationError && (
          <p className="text-sm text-destructive mt-1.5">
            Please enter a valid email address
          </p>
        )}
      </div>

      {/* API error */}
      {error && (
        <div className="flex items-start gap-2 p-3 rounded-lg bg-destructive/10 border border-destructive/20">
          <div className="size-1.5 rounded-full bg-destructive mt-1.5 shrink-0" />
          <p className="text-sm text-destructive">{error}</p>
        </div>
      )}

      <EmailConsentText />

      <Button
        type="submit"
        className="w-full h-12 text-base font-medium group"
        disabled={!isValidEmail || isLoading}
      >
        {isLoading ? (
          <>
            <Loader2Icon className="size-4 animate-spin" />
            <span>Sending code...</span>
          </>
        ) : (
          <>
            <span>Send verification code</span>
            <ArrowRightIcon className="size-4 transition-transform group-hover:translate-x-0.5" />
          </>
        )}
      </Button>
    </form>
  );
}
